import { supabase } from '@/config/supabase';
import type { DailyShopManager } from '@/systems/DailyShopManager';
import type { CosmeticDef } from '@/models/cosmetics';

interface ShopItem {
  id: string;
  name: string;
  price: string;
  description: string;
}

const SHOP_ITEMS: ShopItem[] = [
  { id: 'gems_100', name: '100 Gems', price: '0.99 EUR', description: 'A handful of gems' },
  { id: 'gems_500', name: '500 Gems', price: '3.99 EUR', description: 'A pouch of gems' },
  { id: 'starter_pack', name: 'Starter Pack', price: '1.99 EUR', description: '200 gems + exclusive badge' },
];

const RARITY_COLORS: Record<string, string> = {
  common: '#aaa',
  rare: '#5b9bd5',
  epic: '#a855f7',
  legendary: '#c9a44c',
};

export class ShopPanel {
  private container: HTMLElement;
  private dailySection: HTMLElement;
  private dailyShop: DailyShopManager | null;
  private onBuyCosmetic: ((cosmeticId: string, gemPrice: number) => void) | null;
  private dailyButtons: Map<string, HTMLButtonElement> = new Map();

  constructor(
    parent: HTMLElement,
    dailyShop?: DailyShopManager,
    onBuyCosmetic?: (cosmeticId: string, gemPrice: number) => void,
  ) {
    this.dailyShop = dailyShop ?? null;
    this.onBuyCosmetic = onBuyCosmetic ?? null;

    this.container = document.createElement('div');
    this.container.id = 'shop-panel';
    this.container.className = 'game-panel';
    this.container.style.borderColor = 'rgba(168, 85, 247, 0.5)';

    this.container.innerHTML = `
      <div class="game-panel-header">
        <div class="game-panel-title" style="color: var(--purple);">💎 SHOP</div>
        <button class="game-panel-close">&times;</button>
      </div>
    `;

    // Daily Deals section
    this.dailySection = document.createElement('div');
    this.dailySection.id = 'daily-deals-section';
    this.buildDailyDeals();
    this.container.appendChild(this.dailySection);

    // Separator
    const sep = document.createElement('div');
    sep.style.cssText = 'height: 1px; background: rgba(168,85,247,0.3); margin: 8px 0;';
    this.container.appendChild(sep);

    // IAP label
    const iapLabel = document.createElement('div');
    iapLabel.style.cssText = 'font-size: 11px; color: var(--text-dim); text-align: center; padding: 4px 0;';
    iapLabel.textContent = 'GEM PACKS';
    this.container.appendChild(iapLabel);

    // IAP items
    for (const item of SHOP_ITEMS) {
      const row = document.createElement('div');
      row.style.cssText = `
        display: flex; align-items: center; justify-content: space-between;
        padding: 8px 4px; border-bottom: 1px solid #1a1a2e;
      `;

      const info = document.createElement('div');
      info.innerHTML = `
        <div style="color: #c9a44c;">${item.name}</div>
        <div style="font-size: 10px; color: #666;">${item.description}</div>
      `;

      const btn = document.createElement('button');
      btn.textContent = item.price;
      btn.style.cssText = `
        background: #a855f7; color: white; border: none;
        padding: 6px 12px; font-family: monospace; font-size: 11px;
        border-radius: 4px; cursor: pointer;
        touch-action: manipulation;
      `;
      btn.addEventListener('click', () => this.purchase(item.id));

      row.appendChild(info);
      row.appendChild(btn);
      this.container.appendChild(row);
    }

    this.container.querySelector('.game-panel-close')!.addEventListener('click', () => this.hide());
    parent.appendChild(this.container);
  }

  private buildDailyDeals(): void {
    if (!this.dailyShop) {
      this.dailySection.style.display = 'none';
      return;
    }

    const label = document.createElement('div');
    label.style.cssText = 'font-size: 11px; color: var(--purple); text-align: center; padding: 4px 0; font-weight: 700;';
    label.textContent = 'DAILY DEALS';
    this.dailySection.appendChild(label);

    const items = this.dailyShop.getShopItems();
    this.dailyButtons.clear();

    for (const cosmetic of items) {
      const price = this.dailyShop.getGemPrice(cosmetic);
      const row = document.createElement('div');
      row.style.cssText = `
        display: flex; align-items: center; justify-content: space-between;
        padding: 6px 4px; border-bottom: 1px solid #1a1a2e;
      `;

      const info = document.createElement('div');
      const rarityColor = RARITY_COLORS[cosmetic.rarity] ?? '#aaa';
      info.innerHTML = `
        <div style="color: ${rarityColor}; font-size: 12px;">${cosmetic.name}</div>
        <div style="font-size: 10px; color: #666;">${cosmetic.type} &middot; ${cosmetic.rarity}</div>
      `;

      const btn = document.createElement('button');
      btn.textContent = `💎 ${price}`;
      btn.dataset['cosmeticId'] = cosmetic.id;
      btn.style.cssText = `
        background: rgba(168,85,247,0.2); color: var(--purple); border: 1px solid rgba(168,85,247,0.4);
        padding: 5px 10px; font-family: monospace; font-size: 11px;
        border-radius: 4px; cursor: pointer;
        touch-action: manipulation;
      `;
      btn.addEventListener('click', () => {
        this.onBuyCosmetic?.(cosmetic.id, price);
      });

      this.dailyButtons.set(cosmetic.id, btn);

      row.appendChild(info);
      row.appendChild(btn);
      this.dailySection.appendChild(row);
    }
  }

  update(gems: number, owned: string[]): void {
    if (!this.dailyShop) return;
    const items = this.dailyShop.getShopItems();
    for (const cosmetic of items) {
      const btn = this.dailyButtons.get(cosmetic.id);
      if (!btn) continue;
      const price = this.dailyShop.getGemPrice(cosmetic);
      const isOwned = owned.includes(cosmetic.id);
      const canAfford = gems >= price;

      if (isOwned) {
        btn.textContent = 'Owned';
        btn.disabled = true;
        btn.style.opacity = '0.5';
      } else if (!canAfford) {
        btn.textContent = `💎 ${price}`;
        btn.disabled = true;
        btn.style.opacity = '0.5';
      } else {
        btn.textContent = `💎 ${price}`;
        btn.disabled = false;
        btn.style.opacity = '1';
      }
    }
  }

  private async purchase(productId: string): Promise<void> {
    const { data, error } = await supabase.functions.invoke('create-checkout', {
      body: {
        productId,
        successUrl: window.location.origin + '/?purchase=success',
        cancelUrl: window.location.origin + '/?purchase=cancel',
      },
    });

    if (error || !data?.url) {
      console.error('Checkout error:', error);
      return;
    }

    window.location.href = data.url;
  }

  show(): void { this.container.style.display = 'block'; }
  hide(): void { this.container.style.display = 'none'; }
  toggle(): void {
    this.container.style.display === 'none' ? this.show() : this.hide();
  }
  destroy(): void { this.container.remove(); }
}
